import styles from './invoiceSaleOperationForm.module.css'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, FieldList, FieldType, FormField, FieldValue, O, option, FormSeparator, NumberField, StaticValue, SpinnerDiv, Section, ModalId, useUIActions, Validatable, Field, RadioField } from '../../../../../features/ui'
import { useSystemState, IdentifierType } from '../../../../system'
import { firstItem, toList } from '../../../../../utils/listUtils'
import { FiscalDocumentClass, fiscalDocumentClass, fiscalDocumentHelpers, FiscalDocument, FiscalDocumentDetails, getNetInvoicedHoardValue } from '../../../../fiscal-documents'
import { useActions } from './actions'
import { capitalize, round, toNumber } from '../../../../../utils/typeUtils'
import { FiscalDocumentItemTable } from '../../../../fiscal-documents'
import { FiscalDocumentPreviewBodyResponse, PreviewBodyItem, TaxpayerDataBody } from '../../../../../services/server'
import { Data2, Labels, identifierType, useDataTypesHooks } from '../../../../../features/data-types'
import { InvoicePerceptionTable } from './invoicePerceptionTable'
import { TableName } from '../../../../../tables'
import { PaymentCategory, VatCondition, usePaymentTypeLoaders } from '../../../../administration'
import { Customer, HoardOrder, HoardType, InvoiceMode, SaleOperation, UpdateCustomerAddressModal, UpdateVatConditionModal, hoardHelpers } from '../../../../sales'
import { ErrorType } from '../../../../common'
import { useBranchLoaders } from '../../../../manager'
import { Provincia } from '../../../../../services/gob-ar'
import { invoicePerceptionFields } from './invoicePerceptionFields'
import { DeliveryOrder, useDeliveryHooks } from '../../../../deliveries'
import { usePriceOutlineHooks } from '../../../../operations'
import { useHoardInvoicePreview } from './hoardInvoicePreview'
import { buildPriceAdjustment, getPendingPriceAdjustment, getPreviewItemsTotal, isCoveredByPriceAdjustment, PriceAdjustment, PRICE_ADJUSTMENT_NAME, withoutPriceAdjustmentCreditNotes } from './priceAdjustment'
import { AdjustmentNote } from '../../../state/types'
import { AddressFormModal, addressHelpers, useAddressBuilders, type Address } from '../../../../../features/address'
import { TaxPayerFeedback, TaxPayerLookupFeedback } from './taxPayerLookupFeedback'
import { evaluateTaxPayerLookup, findProvinceName } from './taxPayerLookup'

type Props = {
    table: TableName
    operation: SaleOperation
    customer: Customer
    provinces: Provincia[]
    deliveryOrders?: DeliveryOrder[]
    fiscalDocuments?: FiscalDocument[]
    adjustmentNotes?: AdjustmentNote[]
    fiscalDocumentId?: string
}

export const InvoiceSaleOperationForm = (props: Props) => {
    const {
        table,
        operation,
        customer: initialCustomer,
        provinces,
        deliveryOrders = [],
        fiscalDocuments = [],
        adjustmentNotes = [],
        fiscalDocumentId
    } = props

    const { company } = useSystemState()
    
    const taxIds = useMemo(() => company!.taxIds.filter(t => t.salePoint).map(t => t.taxId), [company])
    const taxIdOptions = useMemo(() => taxIds.map(taxId => option(taxId.toString(), taxId.toString())), [taxIds])
    const defaultCompanyTaxId = useMemo(() => {
        const salePointTaxIds = company!.taxIds.filter(t => t.salePoint)
        const mainTaxId = firstItem(salePointTaxIds.filter(t => t.main).map(t => t.taxId))
        return mainTaxId ?? firstItem(salePointTaxIds.map(t => t.taxId))
    }, [company])
    const initialCustomerEmail = initialCustomer.emails.length > 0 ? initialCustomer.emails[0] : undefined

    const [companyTaxId, setCompanyTaxId] = useState<O<number>>(defaultCompanyTaxId)
    const [customer, setCustomer] = useState<Customer>(initialCustomer)
    const [customerAddress, setCustomerAddress] = useState<O<Address>>(initialCustomer.mainAddress)
    const [customerIdentifier, setCustomerIdentifier] = useState<O<number>>(customer.identifier)
    const [customerName, setCustomerName] = useState<O<string>>(initialCustomer.name)
    const [customerEmail, setCustomerEmail] = useState<O<string>>(initialCustomerEmail)
    const [vatCondition, setVatCondition] = useState<O<VatCondition>>(initialCustomer.vatCondition)
    const [loadingCustomerData, setLoadingCustomerData] = useState<boolean>(false)
    const [taxPayerFeedback, setTaxPayerFeedback] = useState<O<TaxPayerFeedback>>()
    const [padronAddressDraft, setPadronAddressDraft] = useState<O<Data2>>()
    const [isFiscalDataConfirmed, setIsFiscalDataConfirmed] = useState(false)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [previewBody, setPreviewBody] = useState<O<FiscalDocumentPreviewBodyResponse>>()
    const [invoiceError, setInvoiceError] = useState<O<string>>()
    const [saleProvince, setSaleProvince] = useState<O<string>>()
    const [customerGrossIncomeProvinces, setCustomerGrossIncomeProvinces] = useState<string[]>(customer.grossIncomeProvinces || [])
    const [isLoadingSaleProvince, setIsLoadingSaleProvince] = useState(true)
    const [activeFiscalDocumentId, setActiveFiscalDocumentId] = useState<O<string>>(fiscalDocumentId)
    const saleProvinceLoaded = useRef(false)
    const initialSaleProvince = useRef<O<string>>()

    // La clase de comprobante se deriva de la condición de IVA del cliente, no es un estado aparte.
    const fiscalClass = useMemo(
        () => vatCondition ? fiscalDocumentClass(company!.vatCondition, vatCondition) : undefined,
        [company, vatCondition]
    )
    const vatConditionOptions = useMemo(
        () => Object.values(VatCondition).map(value => option(value, Labels.vatConditionLabel(value))),
        []
    )
    // Consumidor Final es una condición de IVA más: no hay un check separado que pueda contradecirla.
    const requiresTaxId =
        !!customerIdentifier &&
        fiscalClass === FiscalDocumentClass.A &&
        identifierType(customerIdentifier) !== IdentifierType.TAX_ID
    const canValidateCuit =
        !!customerIdentifier &&
        identifierType(customerIdentifier) === IdentifierType.TAX_ID &&
        !isFiscalDataConfirmed

    const { buildHoardInvoicePreview } = useHoardInvoicePreview()

    const hoard = table === TableName.HOARD_ORDERS ? operation as HoardOrder : undefined

    // Disponibilidad de cada alcance de facturación del acopio y saldo restante en espacio de valor.
    const invoicing = useMemo(() => {
        if (!hoard) return undefined

        const delivered = hoardHelpers.getInvoiceableItems(hoard, deliveryOrders, InvoiceMode.DELIVERED)
        const total = hoardHelpers.getInvoiceableItems(hoard, deliveryOrders, InvoiceMode.TOTAL)
        const netInvoicedValue = getNetInvoicedHoardValue(withoutPriceAdjustmentCreditNotes(fiscalDocuments, adjustmentNotes), hoard.paymentTypeYield)
        const fullInvoiceIssued = !!hoard.invoiceStats?.fullInvoiceIssued
        const remainingValue = fullInvoiceIssued ?
            0 :
            Math.max(0, round((hoard.value || 0) - netInvoicedValue))
        const isFinancial = hoard.type === HoardType.FINANCIAL

        return {
            delivered,
            total,
            remainingValue,
            deliveredAvailable: !fullInvoiceIssued && delivered.items.length > 0,
            totalAvailable: !fullInvoiceIssued && (isFinancial ? remainingValue > 0.01 : total.items.length > 0),
            isFinancial
        }
    }, [hoard, deliveryOrders, fiscalDocuments, adjustmentNotes])

    // El yield del medio de pago puede ser positivo (recargo) o negativo (descuento).
    const paymentTypeAdjustment = useMemo(() => {
        const paymentTypeYield = hoard?.paymentTypeYield || 0
        if (paymentTypeYield > 0) return 'recargo'
        if (paymentTypeYield < 0) return 'descuento'
        return undefined
    }, [hoard?.paymentTypeYield])

    const [mode, setMode] = useState<InvoiceMode>(() => {
        if (!invoicing) return InvoiceMode.TOTAL

        const { deliveredAvailable, totalAvailable } = invoicing
        const lastMode = hoard?.invoiceStats?.lastMode
        if (lastMode === InvoiceMode.DELIVERED && deliveredAvailable) return lastMode
        if (lastMode === InvoiceMode.TOTAL && totalAvailable) return lastMode
        if (deliveredAvailable) return InvoiceMode.DELIVERED
        if (totalAvailable) return InvoiceMode.TOTAL
        return lastMode ?? InvoiceMode.TOTAL
    })

    const selectedInvoiceable = invoicing ?
        (mode === InvoiceMode.DELIVERED ? invoicing.delivered : invoicing.total) :
        undefined

    const invoicePreviews = hoard && invoicing ? {
        [InvoiceMode.DELIVERED]: buildHoardInvoicePreview(
            table, hoard, InvoiceMode.DELIVERED, invoicing.delivered.items, invoicing.remainingValue
        ),
        [InvoiceMode.TOTAL]: buildHoardInvoicePreview(
            table, hoard, InvoiceMode.TOTAL, invoicing.total.items, invoicing.remainingValue
        )
    } : undefined

    // Línea de bonificación por los ajustes de precio manuales que todavía no se descontaron.
    // En una venta se factura todo de una vez, así que lo que se factura es todo lo que falta facturar.
    const getPriceAdjustment = (baseItems: PreviewBodyItem[]): PriceAdjustment => {
        const documentAmount = getPreviewItemsTotal(baseItems)
        const remainingFullAmount = hoard && invoicePreviews && invoicing?.totalAvailable ?
            getPreviewItemsTotal(invoicePreviews[InvoiceMode.TOTAL].items) :
            documentAmount
        return buildPriceAdjustment(adjustmentNotes, fiscalDocuments, documentAmount, remainingFullAmount)
    }

    const priceAdjustments = invoicePreviews ? {
        [InvoiceMode.DELIVERED]: getPriceAdjustment(invoicePreviews[InvoiceMode.DELIVERED].items),
        [InvoiceMode.TOTAL]: getPriceAdjustment(invoicePreviews[InvoiceMode.TOTAL].items)
    } : undefined

    // Si el ajuste pendiente cubre todo el saldo, el comprobante quedaría en cero: no se ofrece facturar
    const isModeCovered = (invoiceMode: InvoiceMode): boolean =>
        !!invoicePreviews && !!priceAdjustments &&
        isCoveredByPriceAdjustment(
            getPreviewItemsTotal(invoicePreviews[invoiceMode].items),
            priceAdjustments[invoiceMode]
        )

    const selectedPreview = invoicePreviews?.[mode]
    const deliveredCovered = isModeCovered(InvoiceMode.DELIVERED)
    const totalCovered = isModeCovered(InvoiceMode.TOTAL)
    const deliveredAvailable = !!invoicing?.deliveredAvailable && !deliveredCovered
    const totalAvailable = !!invoicing?.totalAvailable && !totalCovered
    const isSelectedModeAvailable = mode === InvoiceMode.DELIVERED ? deliveredAvailable : totalAvailable
    const hasInvoiceableItems = deliveredAvailable || totalAvailable
    const isBalanceCoveredByPriceAdjustment = !!invoicing && !hasInvoiceableItems &&
        (deliveredCovered || totalCovered)

    const { describeFiscalDocumentType } = fiscalDocumentHelpers
    const { formatCuitOrCuil, formatIdentifier, formatPrice } = useDataTypesHooks()

    // El ajuste cubre todo el saldo: el comprobante quedaría en cero y la nota interna queda sin aplicar
    const describeCoveredBalance = (documentAmount: number): string =>
        `El ajuste de precio pendiente (${formatPrice(getPendingPriceAdjustment(adjustmentNotes, fiscalDocuments))}) ` +
        `cubre todo lo que queda por facturar (${formatPrice(documentAmount)}): no hay saldo para facturar.`

    const paymentTypeLoaders = usePaymentTypeLoaders()
    const { getDeliveryAddresses } = useDeliveryHooks()
    const { getPriceOutlineLive } = usePriceOutlineHooks()
    const { describeAddress } = addressHelpers
    const { buildAddress } = useAddressBuilders()

    const { getTaxPayerData, invoicePreview, invoice } = useActions()
    const { openModal } = useUIActions()
    const branchLoaders = useBranchLoaders()

    const showPerceptions = useMemo(() => {
        if (!vatCondition || vatCondition === VatCondition.FINAL_CONSUMER) return false
        const selectedTaxId = company?.taxIds.find(t => t.taxId === companyTaxId)
        if (!selectedTaxId) return false
        const agentProvinces = selectedTaxId.collectionAgentProvinces
        return Array.isArray(agentProvinces) && agentProvinces.length > 0
    }, [companyTaxId, company, vatCondition])
    const canEditPerceptions = showPerceptions && !isFiscalDataConfirmed

    const onAddGrossIncomeProvince = (value?: FieldValue) => {
        const province = value as O<string>
        if (!province || customerGrossIncomeProvinces.includes(province)) return
        setCustomerGrossIncomeProvinces(prev => [...prev, province])
    }

    useEffect(() => {
        if (!showPerceptions || saleProvinceLoaded.current) return

        saleProvinceLoaded.current = true
        setIsLoadingSaleProvince(true)
        const initPerceptionsData = async () => {
            try {
                const addresses = await getDeliveryAddresses(operation)
                let address = addresses.find(a => a.id === operation.deliveryData?.addressId)
                if (!address) {
                    const branch = branchLoaders().findBranch(operation.branchId)!
                    address = branch.address
                }
                setSaleProvince(address.province)
                initialSaleProvince.current = address.province
            } catch {
                saleProvinceLoaded.current = false
                setSaleProvince(undefined)
            } finally {
                setIsLoadingSaleProvince(false)
            }
        }
        initPerceptionsData()
    }, [showPerceptions])

    useEffect(() => {
        if (!invoicing) return

        const modeAvailable = mode === InvoiceMode.DELIVERED ? deliveredAvailable : totalAvailable
        if (modeAvailable) return
        if (deliveredAvailable) setMode(InvoiceMode.DELIVERED)
        else if (totalAvailable) setMode(InvoiceMode.TOTAL)
    }, [mode, invoicing, deliveredAvailable, totalAvailable])

    // En ventas el comprobante sigue llevando todos los ítems vigentes de la operación.
    const buildSalePreviewItems = (): PreviewBodyItem[] => {
        const operationItems = operation.items
            .map(item => ({ ...item, quantity: item.quantity - (item.cancelledQuantity ?? 0) }))
            .filter(item => item.quantity > 0)
        const { items } = getPriceOutlineLive(operationItems, undefined, operation.paymentTypeYield)
        return items.map(item => ({
            productId: item.productId,
            unitSubtotal: item.unitSubtotal,
            unitTotal: item.unitTotal,
            quantity: item.quantity,
            totalWithoutVAT: item.totalWithoutVAT,
            vatAmount: item.vatAmount
        }))
    }

    const loadInvoicePreview = async () => {
        if (hoard && (!selectedPreview || !isSelectedModeAvailable)) {
            setPreviewBody(undefined)
            setInvoiceError(isModeCovered(mode) ?
                describeCoveredBalance(selectedPreview ? getPreviewItemsTotal(selectedPreview.items) : 0) :
                'No hay nada para facturar en este acopio.')
            return
        }

        setLoadingPreview(true)
        setPreviewBody(undefined)
        setInvoiceError(undefined)
        try {
            const baseItems = hoard ? selectedPreview!.items : buildSalePreviewItems()
            const priceAdjustment = getPriceAdjustment(baseItems)
            if (isCoveredByPriceAdjustment(getPreviewItemsTotal(baseItems), priceAdjustment)) {
                setPreviewBody(undefined)
                setInvoiceError(describeCoveredBalance(getPreviewItemsTotal(baseItems)))
                return
            }

            const invoicePreviewBody = await invoicePreview(
                companyTaxId!,
                table,
                operation,
                fiscalClass!,
                customerIdentifier!,
                customerName!,
                vatCondition!,
                saleProvince!,
                {
                    items: baseItems,
                    discounts: priceAdjustment.discounts,
                    fiscalDocumentId: activeFiscalDocumentId,
                    customerGrossIncomeProvinces,
                    customerEmail,
                    customerAddress
                }
            )
            setPreviewBody(invoicePreviewBody)
            setActiveFiscalDocumentId(invoicePreviewBody.fiscalDocumentId)
        } catch (error) {
            setInvoiceError(error instanceof Error ? error.message : 'No se pudo cargar la vista previa de la factura')
        } finally {
            setLoadingPreview(false)
        }
    }

    useEffect(() => {
        if (isFiscalDataConfirmed) {
            setInvoiceError(undefined)
            loadInvoicePreview()
        }
    }, [isFiscalDataConfirmed, mode])

    const resetCustomerData = (identifier: O<number>) => {
        const isInitialCustomer = identifier !== undefined && identifier === initialCustomer.identifier
        setCustomerName(isInitialCustomer ? initialCustomer.name : undefined)
        setCustomerEmail(isInitialCustomer ? initialCustomerEmail : undefined)
        setVatCondition(isInitialCustomer ? initialCustomer.vatCondition : undefined)
        setCustomerAddress(isInitialCustomer ? initialCustomer.mainAddress : undefined)
        setCustomerGrossIncomeProvinces(isInitialCustomer ? initialCustomer.grossIncomeProvinces || [] : [])
        setTaxPayerFeedback(undefined)
        setPadronAddressDraft(undefined)
    }

    const onChangeCompanyTaxId = (value?: FieldValue) => {
        setCompanyTaxId(toNumber(value as O<string>))
    }

    const onChangeCustomerName = (value?: FieldValue) => {
        const name = value as O<string>
        // El Field también llama onChange al sincronizar el valor que setea el padrón:
        // en ese caso el valor ya es el actual y no hay que borrar el feedback.
        if (name !== customerName) setTaxPayerFeedback(undefined)
        setCustomerName(name)
    }

    const onChangeVatCondition = (value?: FieldValue) => {
        const condition = value as O<VatCondition>
        if (condition !== vatCondition) setTaxPayerFeedback(undefined)
        setVatCondition(condition)
    }

    const onChangeCustomerEmail = (value?: FieldValue) => {
        setCustomerEmail(value as O<string>)
        setTaxPayerFeedback(undefined)
    }

    const onChangeCustomerIdentifier = (value?: FieldValue) => {
        const identifier = value as O<number>
        setCustomerIdentifier(identifier)
        resetCustomerData(identifier)
    }

    const onEditCustomerAddress = () => {
        setPadronAddressDraft(undefined)
        setTaxPayerFeedback(undefined)
        if (!isFiscalDataConfirmed) openModal(ModalId.ADDRESS_FORM)
    }

    const onSaveCustomerAddress = (data: Data2) => {
        setPadronAddressDraft(undefined)
        setTaxPayerFeedback(undefined)
        setCustomerAddress(buildAddress(data))
        setTimeout(() => openModal(ModalId.UPDATE_CUSTOMER_ADDRESS), 500)
    }

    const onCustomerAddressUpdated = (updatedCustomer: Customer) => {
        setCustomer(updatedCustomer)
        setCustomerAddress(updatedCustomer.mainAddress)
    }

    const applyTaxPayerData = (data: TaxpayerDataBody, reviewAddress: boolean) => {
        if (data.name) setCustomerName(data.name)
        if (data.vatCondition) setVatCondition(data.vatCondition)
        setTaxPayerFeedback(undefined)

        if (reviewAddress && data.address) {
            setPadronAddressDraft({
                address: capitalize(data.address.street),
                province: findProvinceName(data.address.province, provinces),
                locality: data.address.city
            })
            openModal(ModalId.ADDRESS_FORM)
        } else {
            setTaxPayerFeedback({ type: 'success', message: 'Datos del padrón aplicados.' })
        }
    }

    const onLoadCustomerData = async () => {
        if (!customerIdentifier) return
        setLoadingCustomerData(true)
        setTaxPayerFeedback(undefined)
        try {
            const data = await getTaxPayerData(customerIdentifier.toString(), companyTaxId)

            if (data.cuitValid === false) {
                setTaxPayerFeedback({
                    type: 'warning',
                    message: 'El CUIT no es válido. Podés completar los datos manualmente.'
                })
                return
            }
            if (data.foundInPadron === false) {
                setTaxPayerFeedback({
                    type: 'warning',
                    message: 'El CUIT no figura en el padrón de ARCA. Podés completar los datos manualmente.'
                })
                return
            }

            const evaluation = evaluateTaxPayerLookup(
                { name: customerName, vatCondition, address: customerAddress },
                data
            )

            // Los campos vacíos se completan automáticamente. El domicilio nunca se escribe directo:
            // pasa siempre por el AddressFormModal.
            const nextName = evaluation.name.empty && data.name ? data.name : customerName
            const nextVatCondition = evaluation.vatCondition.empty && data.vatCondition ? data.vatCondition : vatCondition
            if (nextName !== customerName) setCustomerName(nextName)
            if (nextVatCondition !== vatCondition) setVatCondition(nextVatCondition)

            const remainingEvaluation = evaluateTaxPayerLookup(
                { name: nextName, vatCondition: nextVatCondition, address: customerAddress },
                data
            )
            const hasDifferences = remainingEvaluation.name.differing ||
                remainingEvaluation.vatCondition.differing ||
                remainingEvaluation.address.differing
            const hasAddressToReview = remainingEvaluation.address.empty
            const hasMissing = remainingEvaluation.name.missing ||
                remainingEvaluation.vatCondition.missing ||
                remainingEvaluation.address.missing

            if (hasDifferences || hasAddressToReview) {
                setTaxPayerFeedback({ type: 'suggestion', data, evaluation: remainingEvaluation })
            } else if (hasMissing) {
                const missing = [
                    remainingEvaluation.name.missing ? 'el nombre' : undefined,
                    remainingEvaluation.vatCondition.missing ? 'la condición de IVA' : undefined,
                    remainingEvaluation.address.missing ? 'el domicilio' : undefined,
                ].filter(Boolean).join(', ')
                setTaxPayerFeedback({
                    type: 'warning',
                    message: `Datos validados con el padrón de ARCA, pero no devolvió ${missing}. Podés completar los datos manualmente.`
                })
            } else {
                setTaxPayerFeedback({ type: 'success', message: 'Datos validados con el padrón de ARCA.' })
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Could not complete request'
            setTaxPayerFeedback({
                type: 'warning',
                message: `${message}. Podés completar los datos manualmente.`
            })
        } finally {
            setLoadingCustomerData(false)
        }
    }

    const perceptionFields: FormField[] = useMemo(() => invoicePerceptionFields({
        showPerceptions,
        canEditPerceptions,
        provinces,
        saleProvince,
        onChangeSaleProvince: value => setSaleProvince(value as O<string>),
        customerGrossIncomeProvinces,
        onAddGrossIncomeProvince,
        onRemoveGrossIncomeProvince: province => setCustomerGrossIncomeProvinces(
            prev => prev.filter(p => p !== province)
        ),
        isLoadingSaleProvince
    }), [showPerceptions, canEditPerceptions, provinces, saleProvince, customerGrossIncomeProvinces, isLoadingSaleProvince])

    const fiscalFields: FormField[] = useMemo(() => [
        {
            name: 'fiscal-data-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Datos Fiscales" className={styles.separator} />
        },
        {
            name: 'companyTaxId',
            type: FieldType.SELECT,
            label: 'CUIT de Emisor',
            value: companyTaxId?.toString(),
            options: taxIdOptions,
            disabled: isFiscalDataConfirmed,
            onChange: onChangeCompanyTaxId
        },
        {
            name: 'customerIdentifier',
            type: FieldType.TEXT,
            label: 'DNI / CUIT de Cliente',
            render: () => (
                <div className={styles.identifierField}>
                    <NumberField
                        id="customerIdentifier"
                        value={customerIdentifier}
                        defaultValue={customerIdentifier}
                        disabled={isFiscalDataConfirmed}
                        onChange={onChangeCustomerIdentifier}
                    />
                    {canValidateCuit && (
                        <Button
                            label="Validar Cuit"
                            kind="tertiary"
                            icon="repeat"
                            disabled={loadingCustomerData}
                            onClick={onLoadCustomerData}
                        />
                    )}
                </div>
            )
        },
        ...(taxPayerFeedback ? [{
            name: 'taxPayerFeedback',
            type: FieldType.TEXT,
            label: ' ',
            render: () => <TaxPayerLookupFeedback
                feedback={taxPayerFeedback}
                currentName={customerName}
                currentVatCondition={vatCondition}
                provinces={provinces}
                onApply={() => taxPayerFeedback.type === 'suggestion' && applyTaxPayerData(
                    taxPayerFeedback.data,
                    taxPayerFeedback.evaluation.address.empty || taxPayerFeedback.evaluation.address.differing
                )}
                onDiscard={() => setTaxPayerFeedback(undefined)}
            />
        }] : []),
        {
            name: 'customerName',
            type: FieldType.TEXT,
            label: 'Nombre / Razón Social',
            loading: loadingCustomerData,
            render: () => <Field
                field={{
                    name: 'customerName',
                    type: FieldType.TEXT,
                    value: customerName,
                    disabled: isFiscalDataConfirmed
                }}
                onChange={onChangeCustomerName}
            />
        },
        {
            name: 'vatCondition',
            type: FieldType.TEXT,
            label: 'Condición de IVA',
            loading: loadingCustomerData,
            render: () => <Field
                field={{
                    name: 'vatCondition',
                    type: FieldType.SELECT,
                    value: vatCondition,
                    options: vatConditionOptions,
                    disabled: isFiscalDataConfirmed
                }}
                onChange={onChangeVatCondition}
            />
        },
        {
            name: 'email',
            type: FieldType.TEXT,
            label: 'Email',
            render: () => (
                <Field
                    field={{
                        name: 'email',
                        type: FieldType.TEXT,
                        value: customerEmail,
                        disabled: isFiscalDataConfirmed
                    }}
                    onChange={onChangeCustomerEmail}
                />
            )
        },
        {
            name: 'customerAddress',
            type: FieldType.TEXT,
            label: 'Domicilio',
            render: () => (
                <div className={styles.editableField}>
                    {customerAddress && (
                        <p>{describeAddress(customerAddress)}</p>
                    )}
                    <Button
                        label={customerAddress ? '' : 'Agregar'}
                        icon={customerAddress ? 'pencil-fill' : 'plus'}
                        kind="tertiary"
                        small
                        disabled={isFiscalDataConfirmed}
                        onClick={onEditCustomerAddress}
                    />
                </div>
            )
        },
        ...perceptionFields
    ], [companyTaxId, customerEmail, customerIdentifier, customerName, customerAddress, fiscalClass, loadingCustomerData, isFiscalDataConfirmed, taxPayerFeedback, taxIdOptions, perceptionFields, canValidateCuit, vatConditionOptions, vatCondition])


    const onConfirmFiscalData = (confirm: boolean) => () => {
        setIsFiscalDataConfirmed(confirm)
        if (confirm && customer.identifier === customerIdentifier && customer.vatCondition !== vatCondition) {
            openModal(ModalId.UPDATE_VAT_CONDITION)
        }
    }

    const onDiscardChanges = () => {
        setCompanyTaxId(defaultCompanyTaxId)
        setCustomerIdentifier(initialCustomer.identifier)
        resetCustomerData(initialCustomer.identifier)
        setSaleProvince(initialSaleProvince.current)
        setIsFiscalDataConfirmed(false)
        setPreviewBody(undefined)
        setInvoiceError(undefined)
    }

    // Condiciones de venta del comprobante: el server arma los ítems de pago desde la operación.
    const describePaymentItems = (paymentItems?: FiscalDocumentDetails['paymentItems']): string => {
        if (!paymentItems?.length) return 'Sin medio de pago'
        return paymentItems.map(paymentItem => {
            const paymentType = paymentItem.paymentTypeId ?
                paymentTypeLoaders().findPaymentType(paymentItem.paymentTypeId) :
                undefined
            // Un cobro con crédito a favor llega sin medio de pago: el server lo declara como cuenta corriente.
            const name = paymentType?.name || (
                paymentItem.paymentCategory === PaymentCategory.CREDIT_ACCOUNT ?
                    'Crédito a Favor' :
                    Labels.paymentCategoryLabel(paymentItem.paymentCategory)
            )
            const amount = paymentItems.length > 1 ? ` (${formatPrice(paymentItem.amount)})` : ''
            return `${name}${amount}`
        }).join(', ')
    }

    const previewDataFields: FormField[] = useMemo(() => {
        if (!previewBody || !previewBody.fiscalDocumentDetails) return []

        const { perceptions, discounts = [] } = previewBody.fiscalDocumentDetails
        const perceptionFields: FormField[] = perceptions.length > 0 ? [{
            name: 'perceptions',
            type: FieldType.TEXT,
            render: () => <InvoicePerceptionTable perceptions={perceptions} />
        }] : []
        // La bonificación va como descuento del comprobante, no como un ítem negativo
        const discountFields: FormField[] = discounts.length > 0 ? [{
            name: 'discounts',
            type: FieldType.PRICE,
            label: 'Descuento',
            render: () => <StaticValue
                type={FieldType.PRICE}
                value={-discounts.reduce((sum, discount) => sum + discount.amount, 0)}
            />
        }] : []

        const fieldsList: FormField[] = [
            {
                name: 'invoiceType',
                type: FieldType.TEXT,
                label: 'Tipo',
                render: () => (
                    <StaticValue
                        type={FieldType.TEXT}
                        value={describeFiscalDocumentType(previewBody.fiscalDocumentDetails.fiscalData)}
                    />
                )
            },
            {
                name: 'emitter',
                type: FieldType.TEXT,
                label: 'Emisor',
                render: () => {
                    const emitterLabel = `${company!.name} - ${formatCuitOrCuil(previewBody.fiscalDocumentDetails.fiscalData.companyTaxId)}`
                    return <StaticValue type={FieldType.TEXT} value={emitterLabel} />
                }
            },
            {
                name: 'customer',
                type: FieldType.TEXT,
                label: 'Cliente',
                render: () => {
                    const { name, identifier, vatCondition, address } = previewBody.fiscalDocumentDetails.customer
                    const customerLabel = `${name} - ${formatIdentifier(identifier!)}`
                    const vatConditionLabel = Labels.vatConditionLabel(vatCondition!)
                    const addressLabel = address ? describeAddress(address) : customerAddress ? describeAddress(customerAddress) : undefined
                    return <div className={styles.customer}>
                        <span><StaticValue type={FieldType.TEXT} value={customerLabel} /></span>
                        <span><StaticValue type={FieldType.TEXT} value={vatConditionLabel} /></span>
                        {addressLabel && (
                            <span><StaticValue type={FieldType.TEXT} value={addressLabel} /></span>
                        )}
                    </div>
                }
            },
            {
                name: 'paymentType',
                type: FieldType.TEXT,
                label: 'Medio de Pago',
                render: () => <StaticValue
                    type={FieldType.TEXT}
                    value={describePaymentItems(previewBody.fiscalDocumentDetails.paymentItems)}
                />
            },
            {
                name: 'items',
                type: FieldType.TEXT,
                render: () => <FiscalDocumentItemTable items={previewBody.fiscalDocumentDetails.items} />
            },
            ...discountFields,
            ...perceptionFields,
            {
                name: 'vatAmount',
                type: FieldType.PRICE,
                label: 'IVA',
                render: () => {
                    return <StaticValue type={FieldType.PRICE} value={previewBody.fiscalDocumentDetails.totals.vatAmount} />
                }
            },
            {
                name: 'total',
                type: FieldType.PRICE,
                label: 'Total',
                render: () => {
                    return <StaticValue type={FieldType.PRICE} value={previewBody.fiscalDocumentDetails.totals.total} />
                }
            }
        ]
        return fieldsList
    }, [previewBody?.fiscalDocumentDetails, customerAddress])

    const previewFields: FormField[] = useMemo(() => [
        {
            name: 'preview-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Datos de Factura" className={styles.separator} />
        },
        {
            name: 'invoicePreview',
            type: FieldType.TEXT,
            render: () => <Section
                id="invoice-preview"
                space={12}
            >
                <FieldList fields={previewDataFields} />
            </Section>
        }
    ], [previewBody])

    const scopeFields: FormField[] = useMemo(() => {
        if (!hoard || !invoicing || !invoicePreviews) return []

        const deliveredLabel = deliveredAvailable ?
            `Solo productos entregados (${formatPrice(invoicePreviews[InvoiceMode.DELIVERED].creditAmount)})` :
            deliveredCovered ?
                'Solo productos entregados (cubierto por el ajuste de precio)' :
                'Solo productos entregados (no hay entregas sin facturar)'
        const totalLabel = totalAvailable ?
            `Valor total restante (${formatPrice(invoicePreviews[InvoiceMode.TOTAL].creditAmount)})` :
            totalCovered ?
                'Valor total restante (cubierto por el ajuste de precio)' :
                'Valor total restante (no queda saldo sin facturar)'
        const disabledOptions = [
            ...(deliveredAvailable ? [] : [InvoiceMode.DELIVERED]),
            ...(totalAvailable ? [] : [InvoiceMode.TOTAL])
        ]

        const fields: FormField[] = [
            {
                name: 'invoice-scope-separator',
                type: FieldType.TEXT,
                render: () => <FormSeparator title="¿Qué facturar?" className={styles.separator} />
            },
            {
                name: 'invoiceMode',
                type: FieldType.TEXT,
                render: () => <RadioField
                    id="invoice-mode"
                    value={mode}
                    disabled={isFiscalDataConfirmed}
                    options={[
                        option(InvoiceMode.DELIVERED, deliveredLabel),
                        option(InvoiceMode.TOTAL, totalLabel)
                    ]}
                    disabledOptions={disabledOptions}
                    onChange={value => value && setMode(value as InvoiceMode)}
                />
            }
        ]

        if (!selectedPreview) return fields

        // El ajuste de precio pendiente se descuenta recién cuando lo facturado alcanza lo que queda por facturar
        const priceAdjustment = priceAdjustments![mode].amount
        if (priceAdjustment > 0) {
            fields.push({
                name: 'priceAdjustment',
                type: FieldType.PRICE,
                label: PRICE_ADJUSTMENT_NAME,
                render: () => <StaticValue type={FieldType.PRICE} value={-priceAdjustment} />
            })
        }

        // El descuento ya está incluido en el total que va a tener el comprobante
        const paymentTypeFactor = 1 + (hoard.paymentTypeYield || 0) / 100
        const discountedDocumentTotal = round(selectedPreview.documentTotal - priceAdjustment)
        const discountedCreditAmount = round(selectedPreview.creditAmount - priceAdjustment / paymentTypeFactor)

        // En financiero el crédito y el total del comprobante difieren solo por el ajuste del medio
        // de pago (recargo si el yield es positivo, descuento si es negativo). Sin ajuste ambos
        // importes son iguales, así que mostramos un solo campo.
        if (invoicing.isFinancial) {
            fields.push({
                name: 'creditAmount',
                type: FieldType.PRICE,
                label: 'Crédito a facturar',
                render: () => <StaticValue type={FieldType.PRICE} value={discountedCreditAmount} />
            })
            if (paymentTypeAdjustment) {
                fields.push({
                    name: 'documentTotal',
                    type: FieldType.PRICE,
                    label: `Total del comprobante (incluye ${paymentTypeAdjustment} del medio de pago)`,
                    render: () => <StaticValue type={FieldType.PRICE} value={discountedDocumentTotal} />
                })
            }
        } else {
            fields.push({
                name: 'estimatedTotal',
                type: FieldType.PRICE,
                label: 'Total estimado',
                render: () => <StaticValue type={FieldType.PRICE} value={discountedDocumentTotal} />
            })
        }

        return fields
    }, [hoard, invoicing, invoicePreviews, mode, selectedPreview, priceAdjustments, deliveredAvailable, totalAvailable, deliveredCovered, totalCovered, paymentTypeAdjustment, isFiscalDataConfirmed])

    const hasInvoiceInFlight = !!hoard?.invoiceInFlight
    const canGenerateInvoice = !!previewBody?.fiscalDocumentId &&
        (!hoard || (isSelectedModeAvailable && !hasInvoiceInFlight))

    const onGenerateInvoice = async () => {
        if (!previewBody?.fiscalDocumentId) return

        setInvoiceError(undefined)
        try {
            await invoice(table, operation.id, {
                fiscalDocumentId: previewBody.fiscalDocumentId,
                ...(hoard && selectedInvoiceable && {
                    mode,
                    itemQuantities: selectedInvoiceable.items.map(({ item, quantity }) => ({ itemId: item.id, quantity })),
                    fullInvoice: selectedInvoiceable.fullInvoice
                })
            })
        } catch (error) {
            setInvoiceError((error as ErrorType).message)
        }
    }

    return <>
        {hoard && <FieldList fields={scopeFields} />}
        {hoard && !hasInvoiceableItems && !hasInvoiceInFlight && (
            <Validatable
                validations={toList(isBalanceCoveredByPriceAdjustment && invoicePreviews ?
                    describeCoveredBalance(getPreviewItemsTotal(invoicePreviews[totalCovered ? InvoiceMode.TOTAL : InvoiceMode.DELIVERED].items)) :
                    'No hay nada para facturar en este acopio.')}
                expanded
            />
        )}
        {hoard && hasInvoiceInFlight && (
            <Validatable validations={toList('Hay una factura pendiente de autorización. Esperá a que termine para generar otra.')} expanded />
        )}
        <FieldList fields={fiscalFields} />
        {vatCondition && !fiscalClass && (
            <Validatable
                type="warning"
                validations={toList('No se pudo determinar el tipo de comprobante para la condición de IVA seleccionada.')}
                expanded
            />
        )}
        {requiresTaxId && (
            <Validatable
                type="warning"
                validations={toList('Para emitir Factura A el cliente debe estar identificado con CUIT (11 dígitos).')}
                expanded
            />
        )}
        <div className={styles.btnContainer}>
            {!isFiscalDataConfirmed && (
                <Button
                    label='Descartar Cambios'
                    kind='quaternary'
                    icon='arrow-counterclockwise'
                    disabled={loadingCustomerData}
                    onClick={onDiscardChanges}
                />
            )}
            <Button
                label={`${isFiscalDataConfirmed ? 'Editar' : 'Confirmar'} Datos Fiscales`}
                kind='tertiary'
                icon={isFiscalDataConfirmed ? 'pencil' : 'check-lg'}
                disabled={
                    !companyTaxId ||
                    !customerIdentifier ||
                    !customerName ||
                    !vatCondition ||
                    !fiscalClass ||
                    requiresTaxId ||
                    (showPerceptions && (isLoadingSaleProvince || !saleProvince))
                }
                onClick={onConfirmFiscalData(!isFiscalDataConfirmed)}
            />
        </div>
        {isFiscalDataConfirmed && (
            loadingPreview ? (
                <SpinnerDiv label="Cargando previsualización..." />
            ) : (
                <>
                    <FieldList fields={previewFields} />
                    {!invoiceError && (
                        <div className={styles.btnContainer}>
                            <Button
                                label='Generar Factura'
                                kind='quinary'
                                icon='file-earmark-check'
                                disabled={!canGenerateInvoice}
                                onClick={onGenerateInvoice}
                            />
                        </div>
                    )}
                    {invoiceError && (
                        <Validatable validations={toList(invoiceError)} expanded />
                    )}
                </>
            )
        )}
        <UpdateVatConditionModal
            customer={customer}
            vatCondition={vatCondition}
            onSubmit={setCustomer}
        />
        <AddressFormModal
            addressData={padronAddressDraft ?? customerAddress}
            onSubmit={onSaveCustomerAddress}
        />
        <UpdateCustomerAddressModal
            customer={customer}
            address={customerAddress}
            onSubmit={onCustomerAddressUpdated}
        />
    </>
}
